import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from 'react-use-is-online';
import ErrorView from '../ErrorView';
import {
    useCreateUserEmployeeRoleMutation,
    useGetAllSystemAccessRightsQuery
} from '../../Features/usersSlice';
import {
    ShieldCheckIcon,
    CheckCircleIcon,
    ArrowLeftIcon
} from '@heroicons/react/outline';

function CreateUserEmployeeRoleView({}) {
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();

    const user = useSelector(selectCurrentUser);

    const {
        data: access_roles,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetAllSystemAccessRightsQuery();

    const [input, setInput] = useState({
        access_rights: [],
    });

    const [nameInput, setNameInput] = useState({
        name: ''
    });

    const [errors, setErrors] = useState({
        name: '',
        access_rights: '',
    });

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess) {
            setProgress(100);
        }

        return () => {};
    }, [access_roles]);

    const handleNameChange = (e) => {
        setNameInput({
            ...input,
            [e.target.name]: e.target.value
        });
    };

    const handleChange = async (e) => {
        const { value, checked } = e.target;
        const { access_rights } = input;
        const { name } = input;

        if (checked) {
            try {
                setInput({
                    access_rights: [...access_rights, value]
                });
            } catch (err) {
                setProgress(100);
                swal("Action failed", err.data.message, 'error');
            }
        } else {
            setInput({
                access_rights: access_rights.filter((e) => e !== value)
            });
        }
    };

    const [
        createUserEmployeeRole,
        { isLoading: isLoadingSubscribe }
    ] = useCreateUserEmployeeRoleMutation();

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const reqBoby = {
                user_id: user.id,
                access_rights: input.access_rights,
                name: nameInput.name
            };

            setProgress(80);

            const resp = await createUserEmployeeRole(reqBoby).unwrap();

            setProgress(100);
            navigate(-1);

        } catch (err) {
            setProgress(100);

            if (err.data.hasOwnProperty('errors') == true) {
                setErrors({
                    ...errors,
                    name: err.data.errors.name,
                    access_rights: err.data.errors.access_rights,
                });
            } else if (err.data.hasOwnProperty('error') == true) {
                setInput({
                    ...input,
                    errorMassage: err.data.error
                });
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again',
                "error"
            );
        }

        setProgress(100);
        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

                {/* Page Header */}
                <div className="flex items-center gap-3 mb-6">

                    <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <ShieldCheckIcon className="w-6 h-6 text-[#80b643]" />
                    </div>

                    <div>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Create Employee Role
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Create a role and assign system access rights
                        </p>
                    </div>

                </div>

                {/* Main Card */}
                <div className="max-w-3xl mx-auto">

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                        {/* Loading */}
                        {isLoading && (
                            <div className="flex flex-col items-center justify-center py-16">

                                <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                                    Loading access rights...
                                </p>

                            </div>
                        )}

                        {/* Error */}
                        {isError && !isLoading && (
                            <ErrorView />
                        )}

                        {/* Form */}
                        {isSuccess && (
                            <form onSubmit={handleSubmit}>

                                {/* Form Header */}
                                <div className="px-5 sm:px-6 py-5 border-b border-gray-200 dark:border-gray-800">

                                    <div className="flex items-center gap-3">

                                        <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                            <ShieldCheckIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                Role Information
                                            </h2>

                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Give the role a name and select its permissions
                                            </p>
                                        </div>

                                    </div>

                                </div>

                                {/* Role Name */}
                                <div className="p-5 sm:p-6">

                                    <div className="mb-7">

                                        <label
                                            htmlFor="name"
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                        >
                                            Role Name
                                        </label>

                                        <input
                                            className={`w-full px-3.5 py-2.5 rounded-lg border ${
                                                errors.name
                                                    ? 'border-red-300 dark:border-red-700'
                                                    : 'border-gray-300 dark:border-gray-700'
                                            } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                            type="text"
                                            name="name"
                                            id="name"
                                            value={input.name}
                                            onChange={handleNameChange}
                                            placeholder="Enter role name"
                                        />

                                        {errors.name && (
                                            <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                {errors.name}
                                            </p>
                                        )}

                                    </div>

                                    {/* Access Rights */}
                                    <div>

                                        <div className="flex items-center justify-between mb-3">

                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    Access Rights
                                                </h3>

                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    Select the permissions this role should have
                                                </p>
                                            </div>

                                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                                <CheckCircleIcon className="w-5 h-5 text-[#80b643]" />
                                            </div>

                                        </div>

                                        {errors.access_rights && (
                                            <p className="mb-3 text-xs font-medium text-red-600 dark:text-red-400">
                                                {errors.access_rights}
                                            </p>
                                        )}

                                        {/* Access Rights List */}
                                        <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">

                                            {access_roles.data.map((data, index) => (
                                                <label
                                                    key={data.id}
                                                    className={`flex items-center justify-between gap-4 px-4 py-3.5 cursor-pointer transition-colors ${
                                                        index !== access_roles.data.length - 1
                                                            ? 'border-b border-gray-100 dark:border-gray-800'
                                                            : ''
                                                    } hover:bg-gray-50 dark:hover:bg-gray-800/50`}
                                                >

                                                    <div className="flex items-center gap-3 min-w-0">

                                                        <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                                                            <CheckCircleIcon className="w-4 h-4 text-[#80b643]" />
                                                        </div>

                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            {data.name}
                                                        </span>

                                                    </div>

                                                    <input
                                                        type="checkbox"
                                                        name="roles[]"
                                                        value={data.id}
                                                        onChange={handleChange}
                                                        className="w-4 h-4 rounded border-gray-300 text-[#80b643] focus:ring-[#80b643] cursor-pointer"
                                                    />

                                                </label>
                                            ))}

                                        </div>

                                    </div>

                                </div>

                                {/* Actions */}
                                <div className="px-5 sm:px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-800">

                                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                                        <button
                                            type="button"
                                            onClick={back}
                                            disabled={isLoadingSubscribe}
                                            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                                        >
                                            <ArrowLeftIcon className="w-4 h-4" />
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={isLoadingSubscribe}
                                            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium shadow-sm transition-colors"
                                        >
                                            {isLoadingSubscribe && (
                                                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                                            )}

                                            {isLoadingSubscribe
                                                ? 'Saving...'
                                                : 'Save Role'
                                            }
                                        </button>

                                    </div>

                                </div>

                            </form>
                        )}

                    </div>

                </div>

            </div>
        </div>
    );
}

export default CreateUserEmployeeRoleView;